/**
 * Game End Sequencer for Poker Reborn
 *
 * Orchestrates the animation sequence when a hand ends.
 *
 * Flow (per GAME_END_ANIMATION.md):
 *   initialDelay → betToPotDelay (if !fold) →
 *   END-1 (collect + moneyReturn) → postCollectPause →
 *   END-2 (reveal) → cardsRevealedPause →
 *   POT LOOP: [END-3 (highlight) → preAwardPause → END-4 (award)] per pot →
 *   outcomeDisplayPause
 *
 * @see docs/systems/animations/GAME_END_ANIMATION.md - Full specification
 */

import { log } from '@/utils/log';
import { debugOverlay } from '@/utils/DebugOverlay';
import { GAME_END_TIMING } from '@/config/animation';
import { tableState } from '@/state/TableState';
import { targetRegistry } from '@/effects/registry/TargetRegistry';
import {
  ChipCollectToPotEffect,
  type ChipCollectToPotData,
  type PlayerPotSource,
} from '@/effects/library/chips/ChipCollectToPotEffect';
import {
  ChipCollectEffect,
  type ChipCollectData,
} from '@/effects/library/chips/ChipCollectEffect';
// ChipReturnEffect removed - moneyReturn now integrated into END-1
import { gameEventBus } from '@/effects/events/GameEventBus';
import type { Card, ActionType } from '@/types/game';
import { TablePotView } from '@/components/table/TablePotView';
import { audioServiceAdapter } from '@/effects/library/sound/AudioServiceAdapter';

// =============================================================================
// TYPES
// =============================================================================

export interface EndPlayerInfo {
  /** Player ID */
  playerId: string;
  /** Seat number */
  seat: number;
  /** Balance remaining after hand */
  balance: number;
  /** Pot participation amount */
  potParticipation: number;
  /** Money return (uncalled bet) */
  moneyReturn: number;
  /** Has cards to reveal */
  hasCards: boolean;
  /** Hole cards (if revealed) */
  holeCards: [Card, Card] | null;
  /** Core cards - the cards that define the hand (strong highlight) */
  coreCards: Card[] | null;
  /** Kicker cards - supporting cards not part of the hand pattern (subtle highlight) */
  kickerCards: Card[] | null;
  /** Hand name (e.g., "Full House") */
  handName?: string;
}

export interface PotAwardInfo {
  /** Pot ID */
  potId: number;
  /** Winner seat numbers */
  winners: Array<{
    seat: number;
    playerId: string;
    amount: number;
  }>;
  /** Total pot amount */
  potAmount: number;
}

export interface GameEndMessage {
  /** Game number */
  gameNumber: number;
  /** Board cards */
  board: Card[];
  /** Player info at end */
  players: EndPlayerInfo[];
  /** Pot results */
  pots: PotAwardInfo[];
  /** Is showdown (cards revealed) vs fold win */
  isShowdown: boolean;
}

// =============================================================================
// GAME END SEQUENCER CLASS
// =============================================================================

/**
 * Orchestrates the game-end animation sequence.
 *
 * This class manages the animation flow when a hand ends,
 * including card reveals, pot awards, and money returns.
 *
 * Usage:
 * ```typescript
 * gameEndSequencer.playSequence(endMessage);
 * ```
 *
 * The sequence is fire-and-forget - callers should NOT await it.
 * Game state updates happen via callbacks; animations run independently.
 */
export class GameEndSequencer {
  private static _instance: GameEndSequencer | null = null;

  /** Track if a sequence is currently playing */
  private _isPlaying = false;

  /** Captured player bets at sequence start (before state changes clear them) */
  private _capturedPlayerPots: PlayerPotSource[] = [];

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): GameEndSequencer {
    if (!GameEndSequencer._instance) {
      GameEndSequencer._instance = new GameEndSequencer();
    }
    return GameEndSequencer._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    GameEndSequencer._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check if a sequence is currently playing.
   */
  get isPlaying(): boolean {
    return this._isPlaying;
  }

  /**
   * Execute the game-end animation sequence.
   *
   * This is fire-and-forget: callers should NOT await this.
   * The promise is returned for testing purposes only.
   *
   * @param endMessage - The parsed end message data
   * @param hadBettingOnStreet - Whether any betting actions occurred on the current street
   * @param lastActionOnStreet - The last action type on the current street (for betToPotDelay logic)
   * @param capturedBets - Pre-captured player bets (captured at handleEnd start, before state clears)
   * @returns Promise that resolves when the sequence completes
   */
  async playSequence(
    endMessage: GameEndMessage,
    hadBettingOnStreet: boolean,
    lastActionOnStreet: ActionType | null,
    capturedBets?: PlayerPotSource[]
  ): Promise<void> {
    if (this._isPlaying) {
      log.effects.warning`GameEndSequencer: sequence already playing, skipping`;
      debugOverlay.log(`⚠️ GAME_END: already playing, SKIPPED!`);
      return;
    }

    this._isPlaying = true;
    const startTime = Date.now();
    const elapsed = () => `+${Date.now() - startTime}ms`;

    // =========================================================================
    // HEADER
    // =========================================================================
    debugOverlay.log(`════════════════════════════════════════`);
    debugOverlay.log(`🎰 GAME_END SEQUENCE START`);
    debugOverlay.log(`────────────────────────────────────────`);
    debugOverlay.log(`  showdown: ${endMessage.isShowdown}`);
    debugOverlay.log(`  pots to award: ${endMessage.pots.length}`);
    debugOverlay.log(`  hadBetting: ${hadBettingOnStreet}`);
    debugOverlay.log(`  lastAction: ${lastActionOnStreet}`);

    // Use pre-captured bets if provided (captured at handleEnd start),
    // otherwise capture now (fallback, but may be too late due to race conditions).
    if (capturedBets) {
      debugOverlay.log(`  capturedBets: ${capturedBets.length} (from controller)`);
      this._capturedPlayerPots = capturedBets;
      for (const bet of capturedBets) {
        debugOverlay.log(`    seat ${bet.seatNumber}: $${bet.amount} @ (${Math.round(bet.x)},${Math.round(bet.y)})`);
      }
    } else {
      debugOverlay.log(`  capturedBets: NONE (will capture now)`);
      this._capturedPlayerPots = this.capturePlayerPots();
    }
    debugOverlay.log(`────────────────────────────────────────`);

    const potCount = endMessage.pots.length;
    log.effects.info`GameEndSequencer: starting (showdown: ${endMessage.isShowdown}, pots: ${potCount})`;

    try {
      // =========================================================================
      // INITIAL DELAY (can be 0)
      // =========================================================================
      if (GAME_END_TIMING.initialDelay > 0) {
        debugOverlay.log(`${elapsed()} ⏳ initialDelay: ${GAME_END_TIMING.initialDelay}ms`);
        await this.delay(GAME_END_TIMING.initialDelay);
      }

      // =========================================================================
      // COLLECT BETS TO POT (if betting occurred on this street)
      // =========================================================================
      if (hadBettingOnStreet) {
        debugOverlay.log(`${elapsed()} 💰 COLLECT BETS: ${this._capturedPlayerPots.length} player bets → table pot`);

        // betToPotDelay: only if last action was NOT fold
        if (lastActionOnStreet !== 'fold' && GAME_END_TIMING.betToPotDelay > 0) {
          debugOverlay.log(`${elapsed()} ⏳ betToPotDelay: ${GAME_END_TIMING.betToPotDelay}ms (lastAction=${lastActionOnStreet})`);
          await this.delay(GAME_END_TIMING.betToPotDelay);
          debugOverlay.log(`${elapsed()} ✓ betToPotDelay done`);
        } else if (lastActionOnStreet === 'fold') {
          debugOverlay.log(`${elapsed()} ⏭️ betToPotDelay SKIPPED (lastAction=fold)`);
        }

        // END-1: Collect final bets to pot (+ moneyReturn simultaneously)
        debugOverlay.log(`${elapsed()} 🎯 END-1: Collect bets to pot`);
        await this.collectToPot(endMessage);
        debugOverlay.log(`${elapsed()} ✓ END-1 done`);

        // postCollectPause
        if (GAME_END_TIMING.postCollectPause > 0) {
          debugOverlay.log(`${elapsed()} ⏳ postCollectPause: ${GAME_END_TIMING.postCollectPause}ms`);
          await this.delay(GAME_END_TIMING.postCollectPause);
        }
      } else {
        debugOverlay.log(`${elapsed()} ⏭️ COLLECT BETS SKIPPED (no bets to collect)`);
      }

      // =========================================================================
      // SHOWDOWN PATH (if isShowdown)
      // =========================================================================
      if (endMessage.isShowdown) {
        // END-2: Reveal hole cards
        const playersToReveal = endMessage.players.filter(p => p.hasCards && p.holeCards);
        debugOverlay.log(`${elapsed()} 🃏 END-2: Reveal cards (${playersToReveal.length} players)`);
        await this.revealHoleCards(endMessage.players);
        debugOverlay.log(`${elapsed()} ✓ END-2 done`);

        // cardsRevealedPause
        if (GAME_END_TIMING.cardsRevealedPause > 0) {
          debugOverlay.log(`${elapsed()} ⏳ cardsRevealedPause: ${GAME_END_TIMING.cardsRevealedPause}ms`);
          await this.delay(GAME_END_TIMING.cardsRevealedPause);
        }
      } else {
        debugOverlay.log(`${elapsed()} ⏭️ SHOWDOWN PATH SKIPPED (not showdown)`);
      }

      // =========================================================================
      // POT LOOP: END-3 (highlight) + END-4 (award) per pot
      // =========================================================================
      debugOverlay.log(`${elapsed()} 🏆 POT LOOP: ${potCount} pot(s)`);

      for (let i = 0; i < endMessage.pots.length; i++) {
        const pot = endMessage.pots[i];
        if (!pot || pot.winners.length === 0) {
          debugOverlay.log(`${elapsed()}   Pot ${i + 1}: SKIPPED (no winners)`);
          continue;
        }

        const winnerInfo = pot.winners.map(w => `seat${w.seat}=$${w.amount}`).join(', ');
        debugOverlay.log(`${elapsed()}   ─── Pot ${i + 1}/${potCount}: $${pot.potAmount} → ${winnerInfo}`);

        // Inside loop: isShowdown check for highlight
        if (endMessage.isShowdown) {
          // Clear previous pot's highlight (except for first pot)
          if (i > 0) {
            debugOverlay.log(`${elapsed()}   🔄 clearPreviousHighlight`);
            this.clearPreviousHighlight();
          }

          // END-3: Highlight THIS pot's winning cards
          debugOverlay.log(`${elapsed()}   ✨ END-3: Highlight winning cards`);
          await this.highlightWinningCardsForPot(pot, endMessage.players);

          // preAwardPause
          if (GAME_END_TIMING.preAwardPause > 0) {
            debugOverlay.log(`${elapsed()}   ⏳ preAwardPause: ${GAME_END_TIMING.preAwardPause}ms`);
            await this.delay(GAME_END_TIMING.preAwardPause);
          }
        }

        // END-4: Award THIS pot (pass pot index for per-pot targeting)
        debugOverlay.log(`${elapsed()}   💸 END-4: Award pot (index=${i})`);
        await this.awardPotToWinners(pot, i);
        debugOverlay.log(`${elapsed()}   ✓ Pot ${i + 1} awarded`);

        // potAwardStagger between pots (except last)
        if (i < endMessage.pots.length - 1) {
          debugOverlay.log(`${elapsed()}   ⏳ potAwardStagger: ${GAME_END_TIMING.potAwardStagger}ms`);
          await this.delay(GAME_END_TIMING.potAwardStagger);
        }
      }

      // =========================================================================
      // OUTCOME DISPLAY PAUSE
      // =========================================================================
      debugOverlay.log(`${elapsed()} 👁️ outcomeDisplayPause: ${GAME_END_TIMING.outcomeDisplayPause}ms`);
      await this.delay(GAME_END_TIMING.outcomeDisplayPause);

      debugOverlay.log(`────────────────────────────────────────`);
      debugOverlay.log(`✅ GAME_END COMPLETE (total: ${elapsed()})`);
      debugOverlay.log(`════════════════════════════════════════`);
      log.effects.info`GameEndSequencer: end sequence complete`;
    } catch (error) {
      debugOverlay.log(`❌ GAME_END ERROR: ${error}`);
      log.effects.error`GameEndSequencer: error during sequence: ${error}`;
    } finally {
      this._isPlaying = false;
    }
  }

  // ---------------------------------------------------------------------------
  // END-1: Collect Final Bets to Pot (+ moneyReturn simultaneously)
  // ---------------------------------------------------------------------------

  /**
   * Collect any remaining player bets to the table pot.
   * Uses _capturedPlayerPots which was captured at sequence start.
   *
   * MoneyReturn (uncalled bets) will be integrated here in a future update.
   * For now, moneyReturn is handled by the server updating player balances.
   *
   * @see docs/systems/animations/CHIP_ANIMATION_SEAT_PLAYERPOT.md (Direction 2: Return)
   */
  private async collectToPot(_endMessage: GameEndMessage): Promise<void> {
    const particleLayer = targetRegistry.getParticleLayer();
    if (!particleLayer) {
      log.effects.warning`GameEndSequencer: no particle layer for collect`;
      return;
    }

    const tablePotPos = targetRegistry.getTablePotPosition();
    if (!tablePotPos) {
      log.effects.warning`GameEndSequencer: no table pot position`;
      return;
    }

    // Use captured bets from sequence start (not tableState which may be stale)
    const playerPots = this._capturedPlayerPots;

    if (playerPots.length === 0) {
      debugOverlay.log(`collectToPot: no captured bets to collect`);
      return;
    }

    debugOverlay.log(`collectToPot: ${playerPots.length} pots to collect`);

    // Debug: show each captured pot details
    for (const pot of playerPots) {
      debugOverlay.log(`  seat ${pot.seatNumber}: $${pot.amount} from (${Math.round(pot.x)}, ${Math.round(pot.y)})`);
    }
    debugOverlay.log(`  → target: (${Math.round(tablePotPos.x)}, ${Math.round(tablePotPos.y)})`);

    // TODO: Integrate moneyReturn here (chip split animation)
    // For players with moneyReturn > 0:
    //   - Split chips: matched → pot, unmatched → back to seat
    //   - Both animations start simultaneously
    // See: docs/systems/animations/CHIP_ANIMATION_SEAT_PLAYERPOT.md

    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        log.effects.warning`GameEndSequencer: collect effect timed out`;
        resolve();
      }, 2000);

      const collectData: ChipCollectToPotData = {
        container: particleLayer,
        playerPots,
        toX: tablePotPos.x,
        toY: tablePotPos.y,
        potStagger: GAME_END_TIMING.collectStagger,
        onComplete: () => {
          clearTimeout(timeout);
          resolve();
        },
      };

      // Don't pass duration - let ChipCollectToPotEffect calculate it
      // based on collectToPot (400ms) + stagger delays for all pots
      const effect = new ChipCollectToPotEffect(
        { type: 'pot' },
        collectData,
        {} // Let effect calculate proper duration with staggers
      );

      // Play pot collection sound synchronized with animation
      void audioServiceAdapter.play('chips_collect');
      void effect.play();
    });
  }

  // ---------------------------------------------------------------------------
  // END-2: Reveal Hole Cards
  // ---------------------------------------------------------------------------

  /**
   * Reveal hole cards for showdown.
   * Winners' cards are revealed with animation.
   */
  private async revealHoleCards(players: EndPlayerInfo[]): Promise<void> {
    const playersToReveal = players.filter(p => p.hasCards && p.holeCards);

    if (playersToReveal.length === 0) {
      return;
    }

    // Emit showdown:reveal event for each player with staggered timing
    // Views (HoleCardsView/OpponentCardsLayout) subscribe to this event
    for (let i = 0; i < playersToReveal.length; i++) {
      const player = playersToReveal[i];
      if (!player || !player.holeCards) continue;

      // Stagger reveals
      if (i > 0) {
        await this.delay(GAME_END_TIMING.cardRevealStagger);
      }

      // Emit card reveal event (handled by TableScreen/HoleCardsView)
      gameEventBus.emit('showdown:reveal', {
        seat: player.seat,
        playerId: player.playerId,
        cards: player.holeCards,
        mucked: false,
      });

      log.effects.debug`GameEndSequencer: revealed cards for seat ${player.seat}`;
    }

    // Wait for card flip animation to complete
    await this.delay(GAME_END_TIMING.cardReveal);
  }

  // ---------------------------------------------------------------------------
  // END-3: Highlight Winning Cards (per pot)
  // ---------------------------------------------------------------------------

  /**
   * Highlight the winning hand cards for a specific pot.
   * Called once per pot in the pot loop.
   */
  private async highlightWinningCardsForPot(
    pot: PotAwardInfo,
    players: EndPlayerInfo[]
  ): Promise<void> {
    // Get winning cards for THIS pot's winners only
    const potWinnerIds = pot.winners.map((w) => w.playerId);
    const allCoreCards: Card[] = [];
    const allKickerCards: Card[] = [];

    debugOverlay.log(`    potWinnerIds: [${potWinnerIds.join(', ')}]`);

    for (const player of players) {
      const isWinner = potWinnerIds.includes(player.playerId);
      const coreCount = player.coreCards?.length ?? 0;
      const kickerCount = player.kickerCards?.length ?? 0;
      debugOverlay.log(
        `    player ${player.playerId}: winner=${isWinner}, core=${coreCount}, kicker=${kickerCount}`
      );

      if (isWinner) {
        if (player.coreCards) {
          allCoreCards.push(...player.coreCards);
        }
        if (player.kickerCards) {
          allKickerCards.push(...player.kickerCards);
        }
      }
    }

    const totalCards = allCoreCards.length + allKickerCards.length;
    debugOverlay.log(
      `    total cards: ${totalCards} (${allCoreCards.length} core + ${allKickerCards.length} kicker)`
    );

    if (totalCards === 0) {
      debugOverlay.log(`    ⚠️ No winning cards to highlight!`);
      return;
    }

    // Get hand name from first winner (for split pots, all winners have same hand ranking)
    const firstWinnerId = pot.winners[0]?.playerId;
    const winnerPlayer = players.find((p) => p.playerId === firstWinnerId);
    const handName = winnerPlayer?.handName;

    // Emit showdown:highlight event with core/kicker cards for THIS pot
    // Views (HoleCardsView, CommunityCardsView) subscribe to this event
    debugOverlay.log(
      `    📤 Emitting showdown:highlight with ${allCoreCards.length} core + ${allKickerCards.length} kicker, handName: ${handName ?? 'none'}`
    );
    gameEventBus.emit('showdown:highlight', {
      coreCards: allCoreCards,
      kickerCards: allKickerCards,
      potIndex: pot.potId,
      handName,
    });

    log.effects.debug`GameEndSequencer: highlighting ${totalCards} cards (${allCoreCards.length} core + ${allKickerCards.length} kicker) for pot ${pot.potId}`;

    // Wait for highlight animation to be visible
    await this.delay(GAME_END_TIMING.winningCardsHighlight);
  }

  /**
   * Clear the previous pot's highlight before highlighting the next pot.
   */
  private clearPreviousHighlight(): void {
    log.effects.debug`GameEndSequencer: clearing previous highlight`;
    debugOverlay.log(`  clearPreviousHighlight`);
    gameEventBus.emit('showdown:clearHighlight', {});
  }

  // ---------------------------------------------------------------------------
  // END-4: Award Pot
  // ---------------------------------------------------------------------------

  /**
   * Award a single pot to its winner(s).
   * For split pots, chips fly to all winners simultaneously.
   *
   * @param pot - Pot award info
   * @param potIndex - Index of pot being awarded (0 = main, 1+ = side pots)
   */
  private async awardPotToWinners(pot: PotAwardInfo, potIndex: number): Promise<void> {
    const particleLayer = targetRegistry.getParticleLayer();
    if (!particleLayer) {
      log.effects.warning`GameEndSequencer: no particle layer for pot award`;
      return;
    }

    // Get table pot view for per-pot targeting
    const tablePotReg = targetRegistry.getTablePotPosition();
    const tablePotView = tablePotReg?.container instanceof TablePotView
      ? tablePotReg.container
      : null;

    // Get position of the SPECIFIC pot being awarded (not always main pot)
    let fromX: number;
    let fromY: number;

    if (tablePotView) {
      const specificPotPos = tablePotView.getPotChipPosition(potIndex);
      if (specificPotPos) {
        fromX = specificPotPos.x;
        fromY = specificPotPos.y;
        debugOverlay.log(`    pot ${potIndex} position: (${Math.round(fromX)}, ${Math.round(fromY)})`);
      } else {
        // Fallback to main pot position
        fromX = tablePotReg?.x ?? 0;
        fromY = tablePotReg?.y ?? 0;
        debugOverlay.log(`    pot ${potIndex} fallback to main: (${Math.round(fromX)}, ${Math.round(fromY)})`);
      }
    } else if (tablePotReg) {
      fromX = tablePotReg.x;
      fromY = tablePotReg.y;
    } else {
      log.effects.warning`GameEndSequencer: no table pot position for award`;
      return;
    }

    // Hide only THIS specific pot as chips start flying (other side pots stay visible)
    if (tablePotView && !tablePotView.isPotHiddenForAnimation(potIndex)) {
      tablePotView.hidePotForAnimation(potIndex);
      log.effects.debug`GameEndSequencer: hiding pot ${potIndex} for award animation`;
    }

    // Determine if hero is among winners and play appropriate sound
    const heroSeat = tableState.heroSeat.get();
    const heroWins = heroSeat !== null && pot.winners.some(w => w.seat === heroSeat);
    const awardSoundId = heroWins ? 'pot_award_hero' : 'pot_award_opponent';
    void audioServiceAdapter.play(awardSoundId);

    // Create chip animations for all winners simultaneously
    const animationPromises: Promise<void>[] = [];

    for (const winner of pot.winners) {
      const seatReg = targetRegistry.getSeat(winner.seat);
      if (!seatReg) {
        log.effects.warning`GameEndSequencer: no seat registration for winner seat ${winner.seat}`;
        continue;
      }

      // Use ChipCollectEffect to animate chips flying from pot to winner
      const promise = new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          log.effects.warning`GameEndSequencer: pot award effect timed out for seat ${winner.seat}`;
          resolve();
        }, GAME_END_TIMING.potAward + 500);

        const collectData: ChipCollectData = {
          container: particleLayer,
          fromX,
          fromY,
          toX: seatReg.position.x,
          toY: seatReg.position.y,
          amount: winner.amount,
          chipCount: Math.min(10, Math.max(5, Math.floor(winner.amount / 100))),
          onComplete: () => {
            clearTimeout(timeout);
            resolve();
          },
        };

        const effect = new ChipCollectEffect(
          { type: 'seat', id: winner.seat.toString() },
          collectData,
          { duration: GAME_END_TIMING.potAward }
        );

        void effect.play();
      });

      animationPromises.push(promise);

      // Note: player:win event is emitted by GameController.emitEndEvents()
      // We only handle animation timing here, not event emission

      log.effects.debug`GameEndSequencer: awarding ${winner.amount} to seat ${winner.seat}`;
    }

    await Promise.all(animationPromises);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Capture player bets at the start of the sequence.
   * This must be called IMMEDIATELY before any delays to avoid race conditions
   * where state updates clear the bets before collectToPot() runs.
   */
  private capturePlayerPots(): PlayerPotSource[] {
    const playerPots: PlayerPotSource[] = [];
    const players = tableState.players.get();

    debugOverlay.log(`capturePlayerPots: checking ${players.size} players`);

    players.forEach((player, seat) => {
      if (player.bet > 0) {
        const potPos = targetRegistry.getPlayerPotPosition(seat);
        if (potPos) {
          playerPots.push({
            x: potPos.x,
            y: potPos.y,
            amount: player.bet,
            seatNumber: seat,
          });
          debugOverlay.log(`  captured seat ${seat}: $${player.bet} at (${Math.round(potPos.x)}, ${Math.round(potPos.y)})`);
        } else {
          debugOverlay.log(`  seat ${seat}: $${player.bet} - NO POT POSITION!`);
        }
      }
    });

    debugOverlay.log(`capturePlayerPots: total ${playerPots.length} pots captured`);

    return playerPots;
  }

  /**
   * Delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global game end sequencer instance.
 */
export const gameEndSequencer = GameEndSequencer.getInstance();
